import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MessagesPage.css';
import SiteHeader from './components/SiteHeader';
import { sessionManager } from './utils/sessionManager';
import ConversationList from './components/ConversationList';
import MessageThread from './components/MessageThread';
import API_CONFIG from './config';

const MESSAGES_API = {
  BASE_URL: 'https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod',
  ENDPOINTS: {
    CONVERSATIONS: '/conversations',
    CONVERSATION_MESSAGES: '/conversation-messages',
    SEND_MESSAGE: '/send-message',
    CREATE_CONVERSATION: '/create-conversation',
    GET_UNREAD_COUNT: '/get-unread-messages-count',
    MARK_MESSAGES_READ: '/mark-messages-read'
  }
};

const MessagesPage = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [unreadCounts, setUnreadCounts] = useState({});

  const isComponentMounted = useRef(true);

  // Check authentication and get user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      const user = sessionManager.getUserInfo();
      
      if (!user) {
        window.location.href = '/';
        return;
      }



      // Get user profile with ID from backend
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_USER_PREFERENCES}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.authToken}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.profile && data.profile.id) {
            // Use the profile data from backend
            setUserInfo({
              ...user,
              id: data.profile.id,
              firstName: data.profile.firstName,
              lastName: data.profile.lastName,
              role: data.profile.role || user.userRole
            });
          } else {
            setUserInfo(user);
          }
        } else {
          setUserInfo(user);
        }
      } catch (error) {
        setUserInfo(user);
      }
    };

    fetchUserInfo();
  }, []);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchUnreadCounts = useCallback(async (conversationsList) => {
    if (!userInfo?.authToken) return;

    try {
      const response = await fetch(
        `${MESSAGES_API.BASE_URL}${MESSAGES_API.ENDPOINTS.GET_UNREAD_COUNT}?by_conversation=true`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userInfo.authToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.unread_by_conversation) {
          setUnreadCounts(data.unread_by_conversation);
        }
      }
    } catch (err) {
      console.error('Error fetching unread counts:', err);
    }
  }, [userInfo?.authToken]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!userInfo?.authToken || !conversationId) return;

    try {
      const response = await fetch(
        `${MESSAGES_API.BASE_URL}${MESSAGES_API.ENDPOINTS.CONVERSATION_MESSAGES}?conversation_id=${conversationId}&auth_token=${encodeURIComponent(userInfo.authToken)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userInfo.authToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load messages');
      }

      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
      } else {
        throw new Error(data.error || 'Failed to load messages');
      }
    } catch (err) {
      setError(`Error loading messages: ${err.message}`);
      console.error('Error loading messages:', err);
    }
  }, [userInfo?.authToken]);

  const markMessagesAsRead = useCallback(async (conversationId) => {
    if (!userInfo?.authToken) return;

    try {
      await fetch(
        `${MESSAGES_API.BASE_URL}${MESSAGES_API.ENDPOINTS.MARK_MESSAGES_READ}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userInfo.authToken}`
          },
          body: JSON.stringify({
            conversation_id: conversationId
          })
        }
      );

      // Update local unread counts
      setUnreadCounts(prev => ({
        ...prev,
        [conversationId]: 0
      }));
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [userInfo?.authToken]);

  const loadConversations = useCallback(async (options = { showLoader: false }) => {
    if (!userInfo?.authToken) return;

    const { showLoader } = options || {};

    try {
      if (showLoader) {
        setLoading(true);
      }
      const response = await fetch(
        `${MESSAGES_API.BASE_URL}${MESSAGES_API.ENDPOINTS.CONVERSATIONS}?auth_token=${encodeURIComponent(userInfo.authToken)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userInfo.authToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load conversations');
      }

      const data = await response.json();
      if (data.success) {
        const conversationsList = data.conversations || [];
        setConversations(conversationsList);
        
        // Auto-select the first conversation if none is selected and conversations exist
        if (!selectedConversation && conversationsList.length > 0) {
          const firstConversation = conversationsList[0];
          setSelectedConversation(firstConversation);
          loadMessages(firstConversation.conversation_id);
          markMessagesAsRead(firstConversation.conversation_id);
        }
        
        // Fetch unread counts for each conversation
        await fetchUnreadCounts(conversationsList);
      } else {
        throw new Error(data.error || 'Failed to load conversations');
      }
    } catch (err) {
      setError(`Error loading conversations: ${err.message}`);
      console.error('Error loading conversations:', err);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [userInfo?.authToken, fetchUnreadCounts, selectedConversation, loadMessages, markMessagesAsRead]);

  // Load conversations on mount (with loader)
  useEffect(() => {
    if (userInfo) {
      loadConversations({ showLoader: true });
      
      // No real-time polling like ChatPage - just load once
    };
  }, [userInfo, loadConversations]);

  const sendMessage = useCallback(async (conversationId, content) => {
    if (!userInfo?.authToken || !conversationId || !content.trim()) return false;

    try {
      const response = await fetch(
        `${MESSAGES_API.BASE_URL}${MESSAGES_API.ENDPOINTS.SEND_MESSAGE}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userInfo.authToken}`
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            content: content.trim(),
            auth_token: userInfo.authToken
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      if (data.success) {
        return true;
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError(`Error sending message: ${err.message}`);
      console.error('Error sending message:', err);
      return false;
    }
  }, [userInfo?.authToken]);

  const handleConversationSelect = useCallback((conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.conversation_id);
    
    // Mark messages as read when conversation is opened
    markMessagesAsRead(conversation.conversation_id);
    
    // No real-time polling at all - like ChatPage
  }, [loadMessages, markMessagesAsRead]);

  const handleBackToList = () => {
    // No polling to stop
    setSelectedConversation(null);
    setMessages([]);
  };

  const handleSendMessage = async (content) => {
    if (!selectedConversation || !content.trim()) return false;
    
    // EXACTLY like ChatPage - add user message immediately
    const userMessage = {
      id: `temp_${Date.now()}`,
      content: content.trim(),
      sender_id: userInfo?.id,
      sender_name: userInfo?.firstName || userInfo?.phoneNumber || 'You',
      created_at: new Date().toISOString(),
      message_type: 'text'
    };
    
    // Add to state immediately like ChatPage
    setMessages(prev => [...prev, userMessage]);
    
    // Send to backend
    try {
      const success = await sendMessage(selectedConversation.conversation_id, content);
      
      if (!success) {
        // Remove the message if sending failed
        setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
        return false;
      }
      
      // Refresh messages from backend to replace optimistic message with real one
      await loadMessages(selectedConversation.conversation_id);
      // Silently refresh conversations to update sidebar latest message preview
      loadConversations({ showLoader: false });
      
      return true;
    } catch (error) {
      console.error('Send message error:', error);
      // Remove the message if sending failed
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
      return false;
    }
  };



  // Cleanup on unmount
  useEffect(() => {
    isComponentMounted.current = true;
    
    return () => {
      isComponentMounted.current = false;
      // No cleanup needed since no polling
    };
  }, []);

  if (!userInfo) {
    return <div className="messages-page loading">Loading...</div>;
  }

  return (
    <div className="messages-page">
      {/* Reusable site header (from homepage) */}
      <SiteHeader />

      <div className="messages-content">


        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading conversations...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p className="error-message">{error}</p>
            <button onClick={loadConversations} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Mobile: Show either list or thread */}
            {isMobile ? (
              <>
                {!selectedConversation ? (
                  <div className="mobile-conversation-list">
                    <ConversationList
                      conversations={conversations}
                      onSelect={handleConversationSelect}
                      userInfo={userInfo}
                      unreadCounts={unreadCounts}
                    />
                  </div>
                ) : (
                  <div className="mobile-message-thread">
                    <MessageThread
                      conversation={selectedConversation}
                      messages={messages}
                      onSendMessage={handleSendMessage}
                      onBack={handleBackToList}
                      userInfo={userInfo}
                      isMobile={true}
                    />
                  </div>
                )}
              </>
            ) : (
              /* Desktop: Show both list and thread side by side */
              <div className="desktop-layout">
                <div className="conversation-list-panel">
                  <ConversationList
                    conversations={conversations}
                    onSelect={handleConversationSelect}
                    selectedConversationId={selectedConversation?.conversation_id}
                    userInfo={userInfo}
                    unreadCounts={unreadCounts}
                  />
                </div>
                <div className="message-thread-panel">
                  {selectedConversation ? (
                    <MessageThread
                      conversation={selectedConversation}
                      messages={messages}
                      onSendMessage={handleSendMessage}
                      userInfo={userInfo}
                      isMobile={false}
                    />
                  ) : (
                    <div className="no-conversation-selected">
                      <div className="placeholder-content">
                        <div className="placeholder-icon">💬</div>
                        <h3>Select a conversation to start messaging</h3>
                        <p>Choose a property conversation from the list to view your messages and continue the conversation.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty state when no conversations */}
        {!loading && !error && conversations.length === 0 && (
          <div className="empty-state">
            <div className="empty-content">
              <div className="empty-icon">📭</div>
              <h3>No conversations yet</h3>
              <p>When you apply to rental properties, your conversations with agents will appear here.</p>
              <button 
                className="browse-properties-button"
                onClick={() => window.location.href = '/'}
              >
                Browse Properties
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
